import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { ExplorableItemType } from '../../../ts/models/ContentType';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '../../../ts/models/sort/SortOptionDefinitions';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { setOrderAction } from '../../actions/SetOrderAction';
import { sortContent } from '../../actions/spaces/SortContentAction';
import { useIsMounted, usePrevious } from '../../hooks';
import { selectCurrentlyApplicableContentTypes } from '../../selectors/ContentFilterSelectors';
import { selectSpaceInfo } from '../../selectors/ContentSelectors';
import { SpaceInfo } from '../../state/SpaceInfo';
import { UrlParamKeys } from '../../UrlParamKeys';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { getOrderClausesFromQueryParam } from '../Explorer/ExplorerUrlBindings';

export interface SortOrderUpdaterOwnProps {
  readonly routeLocationSearch: string;
  readonly routeLocationPath: string;
  readonly resetStore?: boolean;
}

export interface SortOrderUpdaterStateProps {
  readonly contentTypes: ReadonlyArray<ExplorableItemType>;
  readonly spaceInfo: SpaceInfo;
}

export interface SortOrderUpdaterDispatchProps {
  readonly setOrderAction: (order: IOrderClause[]) => void;
  readonly sortContent: (order: IOrderClause[]) => void;
}

export type SortOrderUpdaterProps = SortOrderUpdaterOwnProps
  & SortOrderUpdaterStateProps
  & SortOrderUpdaterDispatchProps;

/**
 * Component that syncs the sort order from the URL query params to the redux store before rendering its children.
 */
export const SortOrderUpdater: React.FunctionComponent<SortOrderUpdaterProps> = props => {
  const isMounted = useIsMounted();
  const prevRouteLocationPath = usePrevious(props.routeLocationPath);
  const queryParams = queryString.parse(props.routeLocationSearch);
  const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(props.contentTypes, props.spaceInfo);
  const orderFromQuery = getOrderClausesFromQueryParam(queryParams[UrlParamKeys.Order], defaultOrder);

  // Before the initial render, initialize the sort order in the app store
  // TFSID:983648.  Handle case when updating this component with a new projectId
  if (!isMounted || props.resetStore) {
    props.setOrderAction(orderFromQuery);
  }

  // When the order query param changes, update the sort order in the app store
  React.useEffect(() => {
    if (isMounted && prevRouteLocationPath === props.routeLocationPath) {
      props.sortContent(orderFromQuery);
    }
  }, [ queryParams[UrlParamKeys.Order] ]);

  return <>{props.children}</>;
};

const mapStateToProps: MapStateToProps<SortOrderUpdaterStateProps> = state => ({
  contentTypes: selectCurrentlyApplicableContentTypes(state),
  spaceInfo: selectSpaceInfo(state),
});

const mapDispatchToProps: MapDispatchToProps<SortOrderUpdaterDispatchProps> = dispatch => bindActionCreators({
  setOrderAction,
  sortContent,
}, dispatch);

export const ConnectedSortOrderUpdater = connect(mapStateToProps, mapDispatchToProps)(SortOrderUpdater);

/**
 * Creates a higher order component that wraps the given component with the SortOrderUpdate component.
 */
export function withSortOrderUpdater<P>(Component: React.ComponentType<P>) {
  return (props: P & SortOrderUpdaterProps) => (
    <ConnectedSortOrderUpdater {...props}><Component {...props}/></ConnectedSortOrderUpdater>
  );
}
